import React, { useState, useRef } from 'react';
import { sparkToast, SparkModalPortal, Modal as SparkModal, ReadOnlyGuard, useReadOnly } from '@spark';
import { ActivityType } from '@/shared/domain/taskTypes';
import RelationshipCreator from '@/features/datasets/components/DataSelection/RelationshipCreator';
import { getTextDirectionStyle } from '@/shared/utils/textUtils';
import RelationList from '@/features/datasets/components/DataSelection/RelationList';
import NerTaggerSurface from '@/features/datasets/components/DataSelection/NerTaggerSurface';
import EntityTagLegend from '@/features/datasets/components/DataSelection/EntityTagLegend';
import NerTagDeleteConfirmModal from '@/features/datasets/components/DataSelection/NerTagDeleteConfirmModal';
import useNerTagCatalog from '@/features/datasets/hooks/useNerTagCatalog';
import { useAuth } from '@/features/auth/AuthContext';
import '@/features/datasets/components/DataSelection/NERTagger.css';
export default function NERTagger({
    text,
    entities = [],
    entityOptions = [],
    onChange,
    isFullScreen = false,
    relationships = [],
    dataset,
    refreshCategories,
    relationshipMode = false,
    client_id,
    relationshipTypes,
    refreshRelationshipTypes,
    recordId,
    updateRecord,
    llm_usecase_id,
    onRefreshRecords,
    fetchValidationCounts,
    isBenchmark = false,
}) {
    const { role } = useAuth();
    const { readOnly } = useReadOnly();
    const [editingEntity, setEditingEntity] = useState(null);
    const [showRelationshipModal, setShowRelationshipModal] = useState(false);
    const [sourceEntity, setSourceEntity] = useState();
    const [targetEntity, setTargetEntity] = useState();
    const textRef = useRef();
    // Legend tag chosen for "focus mode": dims the modal and spotlights the
    // selected tag + the annotation surface.
    const [selectedLegendTag, setSelectedLegendTag] = useState(null);
    const [tooltipKey, setTooltipKey] = useState(0);
    // Text span the user marked in the surface, pending a tag choice from the
    // "Available tags" legend.
    const [selectedText, setSelectedText] = useState(null);

    // Focus mode ("go dark") is active in either direction: a legend tag is
    // selected (pick-tag-then-mark) or a text span is marked (mark-then-pick-tag).
    // In both cases the modal dims and only the spotlighted elements stay lit.
    const isFocusMode = !!selectedLegendTag || !!selectedText;

    // Leave focus mode: drop any pending tag/selection and clear the browser's
    // text highlight so nothing stays marked behind the scrim.
    const exitFocusMode = () => {
        setSelectedLegendTag(null);
        setSelectedText(null);
        window.getSelection()?.removeAllRanges();
    };

    // In focus mode, ESC exits focus mode instead of closing the annotate modal.
    // The capture-phase listener runs before (and stops propagation to) the
    // modal's own ESC handler, so the outer modal keeps its dark backdrop.
    React.useEffect(() => {
        if (!isFocusMode) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                setSelectedLegendTag(null);
                setSelectedText(null);
                window.getSelection()?.removeAllRanges();
            }
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [isFocusMode]);

    const onEntityAdd = (entity) => {
        if (entities.some(e =>
            e.start_index >= entity.start_index && e.start_index < entity.start_index + entity.content.length
        )) {
            sparkToast.error('Cannot tag overlapping entities');
            return;
        }
        onChange([...entities, entity], relationships, ActivityType.entity_added, `entity: ${entity.content}, label: ${entity.label}`);
    };

    // Resolve the current text selection inside the annotation surface into a
    // { text, start } span, mapping the DOM range to a character start index via
    // the nearest data-index anchor. Returns null when there is no valid range.
    const computeSelection = () => {
        const selectionObj = window.getSelection();
        if (selectionObj.isCollapsed || !textRef.current?.contains(selectionObj.anchorNode)) return null;
        const selectedText = selectionObj.toString();
        if (!selectedText) return null;
        const getIndexFromNode = (node, offset) => {
            let el = node.nodeType === 3 ? node.parentElement : node;
            while (el && !el.getAttribute?.('data-index') && el !== textRef.current) {
                el = el.parentElement;
            }
            const base = el && el.getAttribute ? parseInt(el.getAttribute('data-index'), 10) : 0;
            return node.nodeType === 3 ? base + offset : base;
        };
        const anchorIndex = getIndexFromNode(selectionObj.anchorNode, selectionObj.anchorOffset);
        const focusIndex = getIndexFromNode(selectionObj.focusNode, selectionObj.focusOffset);
        return {
            selectionObj,
            text: selectedText,
            start: Math.min(anchorIndex, focusIndex),
        };
    };

    // Tag the current selection directly with an already-chosen label — used when
    // a tag is pre-selected in the "Available tags" legend. Silently no-ops on
    // empty selection or exact duplicate.
    const tagSelectionWithLabel = (label) => {
        const sel = computeSelection();
        if (!sel || !label) return;
        if (entities.some(ent => ent.start_index === sel.start && ent.content === sel.text && ent.label === label)) return;
        onEntityAdd({
            start_index: sel.start,
            content: sel.text,
            label,
            is_validated: true,
        });
        sel.selectionObj.removeAllRanges();
    };

    // Annotation surface mouse-up. When a legend tag is selected (focus mode),
    // tag the selection with it directly; otherwise capture the selection as
    // pending, so the user can apply a tag from the "Available tags" legend.
    const handleHighlightFirst = () => {
        if (selectedLegendTag) {
            tagSelectionWithLabel(selectedLegendTag);
            return;
        }
        const sel = computeSelection();
        if (!sel) return;
        setSelectedText({ text: sel.text, start_index: sel.start });
        sel.selectionObj.removeAllRanges();
    };

    // Apply a chosen tag to the pending marked selection (clicked in the
    // "Available tags" legend). No-ops when nothing is marked.
    const applyTagToSelection = (category) => {
        if (!selectedText) return;
        if (entities.some(ent => ent.start_index === selectedText.start_index && ent.content === selectedText.text && ent.label === category)) {
            sparkToast.error('Cannot tag overlapping entities');
            return;
        }
        onEntityAdd({
            start_index: selectedText.start_index,
            content: selectedText.text,
            label: category,
            is_validated: true,
        });
        window.getSelection().removeAllRanges();
        setSelectedText(null);
    };

    // Legend tag click. If the user already marked text, tag that pending
    // selection; otherwise enter focus mode (pick a tag, then mark text).
    const handleLegendTagSelect = (tag) => {
        if (tag && selectedText) {
            applyTagToSelection(tag);
            return;
        }
        setSelectedLegendTag(tag);
    };

    // "Available tags" catalog: counts, add-tag and delete-tag flows shared
    // with the editable annotate body (NerAnnotateEditor).
    const { allCategories, categoryCounts, addTag, requestDeleteTag, deleteConfirm } = useNerTagCatalog({
        entityOptions,
        entityLabels: entities.map((ent) => ent.label),
        llm_usecase_id,
        dataset,
        refreshCategories,
        fetchValidationCounts,
        onRefreshRecords,
        updateRecord,
        isBenchmark,
        recordId,
        onCategoryDeleted: (cat, result) => {
            if (selectedLegendTag === cat) {
                setSelectedLegendTag(null);
            }
            if (result?.new_record?._id && updateRecord) {
                // Server returned the refreshed record; updateRecord already ran.
                return;
            }
            if (entities.some((ent) => ent.label === cat)) {
                onChange(
                    entities.filter((ent) => ent.label !== cat),
                    relationships,
                    ActivityType.entity_deleted,
                    `category: ${cat}`,
                );
            }
        },
    });
    // Handler for new relationship creation
    const handleCreateRelationship = (rel) => {
        const getEntityByStartIndex = (start_index) => entities.find(e => e.start_index === start_index);
        const srcEnt = getEntityByStartIndex(rel.source_start_index);
        const tgtEnt = getEntityByStartIndex(rel.target_start_index);
        const activity_comment = `relationship: ${srcEnt?.content || rel.source_start_index} -> ${rel.label} -> ${tgtEnt?.content || rel.target_start_index}`;
        onChange(entities, [...relationships, rel], ActivityType.relationship_added, activity_comment);
        setSourceEntity();
        setTargetEntity();
        setShowRelationshipModal(false);
        setTooltipKey(k => k + 1);
    };

    // Handler to exit relationship mode
    const exitRelationshipMode = () => {
        setSourceEntity();
        setTargetEntity();
        setShowRelationshipModal(false);
        setTooltipKey(k => k + 1);
    };

    // Handler to toggle validation status
    const handleToggleValidation = (ent) => {
        const updatedEntities = entities.map(e =>
            e === ent ? { ...e, is_validated: !e.is_validated } : e
        );
        const activity_type = !ent.is_validated ? ActivityType.validated_entity : ActivityType.unvalidated_entity;
        onChange(updatedEntities, relationships, activity_type, `entity: ${ent.content}, label: ${ent.label}`);
    };

    const handleDeleteEntity = (entityToDelete) => {
        const updatedEntities = entities.filter(ent => ent !== entityToDelete);
        const activity_type = ActivityType.entity_deleted;
        const activity_comment = `entity: ${entityToDelete.content}, label: ${entityToDelete.label}`;
        onChange(updatedEntities, relationships, activity_type, activity_comment);
        setEditingEntity(null);
    };

    const handleDeleteRelation = (rel, idx) => {
        setPendingRelDelete({ rel, idx });
        setConfirmRelDeleteVisible(true);
    };

    const confirmRelDelete = () => {
        if (!pendingRelDelete) return;
        const { rel, idx } = pendingRelDelete;
        const updated = relationships.filter((_, i) => i !== idx);
        const activity_type = ActivityType.relationship_deleted;
        const getEntityByStartIndex = (start_index) => entities.find(e => e.start_index === start_index);
        const srcEnt = getEntityByStartIndex(rel.source_start_index);
        const tgtEnt = getEntityByStartIndex(rel.target_start_index);
        const activity_comment = `relationship: ${srcEnt?.content || rel.source_start_index} -> ${rel.label} -> ${tgtEnt?.content || rel.target_start_index}`;
        onChange(entities, updated, activity_type, activity_comment);
        setConfirmRelDeleteVisible(false);
        setPendingRelDelete(null);
    };

    const cancelRelDelete = () => {
        setConfirmRelDeleteVisible(false);
        setPendingRelDelete(null);
    };

    // Handle bulk deletion of relations
    const handleBulkDeleteRelations = (selectedIndices) => {
        // Create a new array with only the relations that are NOT selected
        const updated = relationships.filter((_, idx) => !selectedIndices.includes(idx));
        const deleted = relationships.filter((_, idx) => selectedIndices.includes(idx));
        const activity_type = ActivityType.relationship_bulk_deleted;
        const activity_comment = `relationships: \n   ${deleted.map(rel => {
            const getEntityByStartIndex = (start_index) => entities.find(e => e.start_index === start_index);
            const srcEnt = getEntityByStartIndex(rel.source_start_index);
            const tgtEnt = getEntityByStartIndex(rel.target_start_index);
            return `relationship: ${srcEnt?.content || rel?.source_start_index} -> ${rel?.label} -> ${tgtEnt?.content || rel?.target_start_index}`;
        }).join(', \n   ')}`;
        onChange(entities, updated, activity_type, activity_comment);
    };

    // Handle bulk validate of relations
    const handleBulkValidateRelations = (selectedIndices) => {
        const updated = relationships.map((rel, idx) => selectedIndices.includes(idx) ? { ...rel, is_validated: true } : rel);
        const validated = relationships.filter((_, idx) => selectedIndices.includes(idx));
        const activity_type = ActivityType.relationships_validated;
        const activity_comment = `relationships: \n   ${validated.map(rel => {
            const getEntityByStartIndex = (start_index) => entities.find(e => e.start_index === start_index);
            const srcEnt = getEntityByStartIndex(rel.source_start_index);
            const tgtEnt = getEntityByStartIndex(rel.target_start_index);
            return `relationship: ${srcEnt?.content || rel?.source_start_index} -> ${rel?.label} -> ${tgtEnt?.content || rel?.target_start_index}`;
        }).join(', \n   ')}`;
        onChange(entities, updated, activity_type, activity_comment);
    };

    // Handle bulk unvalidate of relations
    const handleBulkUnvalidateRelations = (selectedIndices) => {
        const updated = relationships.map((rel, idx) => selectedIndices.includes(idx) ? { ...rel, is_validated: false } : rel);
        const unvalidated = relationships.filter((_, idx) => selectedIndices.includes(idx));
        const activity_type = ActivityType.relationships_unvalidated;
        const activity_comment = `relationships: \n   ${unvalidated.map(rel => {
            const getEntityByStartIndex = (start_index) => entities.find(e => e.start_index === start_index);
            const srcEnt = getEntityByStartIndex(rel.source_start_index);
            const tgtEnt = getEntityByStartIndex(rel.target_start_index);
            return `relationship: ${srcEnt?.content || rel?.source_start_index} -> ${rel?.label} -> ${tgtEnt?.content || rel?.target_start_index}`;
        }).join(', \n   ')}`;
        onChange(entities, updated, activity_type, activity_comment);
    };

    const [confirmRelDeleteVisible, setConfirmRelDeleteVisible] = useState(false);
    const [pendingRelDelete, setPendingRelDelete] = useState(null);

    const swapSourceAndTarget = () => {
        setSourceEntity(targetEntity);
        setTargetEntity(sourceEntity);
    };
    return (
        <>
            <div className={['ner-tagger', isFocusMode ? 'ner-tagger--focus' : '', selectedText ? 'ner-tagger--focus-text' : ''].filter(Boolean).join(' ')}>
            {isFocusMode && (
                <div
                    className="ner-tagger-scrim"
                    onClick={exitFocusMode}
                />
            )}
            <div className="ner-tagger-segment">
                <div className="ner-tagger-instruction" aria-hidden={!selectedLegendTag}>
                    {selectedLegendTag
                        ? `Highlight the text to tag as "${selectedLegendTag}"`
                        : '\u00A0'}
                </div>
                <ReadOnlyGuard block>
                    <NerTaggerSurface
                        ref={textRef}
                        text={text}
                        entities={entities}
                        allCategories={allCategories}
                        isFullScreen={isFullScreen}
                        editingEntity={editingEntity}
                        onMouseUp={handleHighlightFirst}
                        refreshKey={tooltipKey}
                        relationshipMode={relationshipMode}
                        sourceEntity={sourceEntity}
                        targetEntity={targetEntity}
                        handleToggleValidation={handleToggleValidation}
                        handleDeleteEntity={handleDeleteEntity}
                        setSourceEntity={setSourceEntity}
                        setTargetEntity={setTargetEntity}
                        setShowRelationshipModal={setShowRelationshipModal}
                        getTextDirectionStyle={getTextDirectionStyle}
                        markedSpan={selectedText}
                    />
                </ReadOnlyGuard>
            </div>
            <EntityTagLegend
                allCategories={allCategories}
                categoryCounts={categoryCounts}
                onDeleteCategory={requestDeleteTag}
                onAddTag={addTag}
                onSelectTag={handleLegendTagSelect}
                selectedTag={selectedLegendTag}
                canDelete={!readOnly && role !== 'Viewer'}
                hasPendingSelection={!!selectedText}
            />
            {isFullScreen && relationships && relationships.length > 0 && (
                <RelationList
                    relations={relationships}
                    entities={entities}
                    onDelete={handleDeleteRelation}
                    onBulkDelete={handleBulkDeleteRelations}
                    allCategories={allCategories}
                    onBulkValidate={handleBulkValidateRelations}
                    onBulkUnvalidate={handleBulkUnvalidateRelations}
                />
            )}
            </div>
            {relationshipMode && (
                <RelationshipCreator
                    open={showRelationshipModal}
                    source={sourceEntity}
                    target={targetEntity}
                    onCreate={handleCreateRelationship}
                    onCancel={exitRelationshipMode}
                    relationshipTypes={relationshipTypes}
                    client_id={client_id}
                    dataset={dataset}
                    onAddType={refreshRelationshipTypes}
                    swapSourceAndTarget={swapSourceAndTarget}
                    llm_usecase_id={llm_usecase_id}
                    updateRecord={updateRecord}
                    record_id={recordId}
                    allCategories={allCategories}
                />
            )}
            <SparkModalPortal open={confirmRelDeleteVisible} onClose={cancelRelDelete} size="text">
                <SparkModal
                    className="ner-confirm-modal"
                    title="Delete this relationship?"
                    showSubtitle={false}
                    showScrollBar={false}
                    showFooter
                    danger
                    onPrimaryClick={confirmRelDelete}
                    onTertiaryClick={cancelRelDelete}
                    primaryLabel="Delete"
                    primaryDisableWhenReadOnly
                    tertiaryLabel="Cancel"
                    onCloseClick={cancelRelDelete}
                >
                    <div className="ner-confirm-modal-body">
                        <div>Are you sure you want to delete this relationship?</div>
                        <div>This action cannot be undone.</div>
                    </div>
                </SparkModal>
            </SparkModalPortal>
            <NerTagDeleteConfirmModal {...deleteConfirm} />
        </>
    );
} 